/* eslint-disable max-lines */
import { withPreloader } from '../UnicronApp/preloader/withPreloader';
import { CookiesProvider } from '../common/CookiesProvider/CookiesProvider';
import { preloadExperiments } from '../common/ExperimentContext/preload/preloadExperiments';
import { AnnouncementBannerExperiment } from '../common/Experiments/AnnouncementBannerExperiment';
import { DesktopTopNavExperiment } from '../common/Experiments/DesktopTopNavExperiment';
import { HomepageTilesExperiment } from '../common/Experiments/HomepageTilesExperiment';
import { ForceFullReload } from '../common/components/ForceFullReload';
import { useSimpleFlagr } from '../common/services/FlagrService/useFlagr';
import { LoggerService } from '../common/services/LoggerService/LoggerService';
import { Flags, useFlags } from '../common/services/flags';
import { PromiseBag } from '../common/util/PromiseBag';
import { env } from '../common/util/env';
import { clientIP } from '../common/util/officeIPFilter';
import { proxyToHost } from '../common/util/proxyToHost';
import { BrazeWrapper } from '../common/vendors/braze/BrazeWrapper';
import { Homepage } from './Page/Homepage';
import { preloadPromotedSpecialties } from './preloadPromotedSpecialties';

const HOMEPAGE_FLAG = 'kqyk51yd3mm46qkyc-react-homepage';

export const HomepageEntryPoint = withPreloader(
  () => {
    const willShowUnicronHomepageFlag = useSimpleFlagr(HOMEPAGE_FLAG);

    const flags = useFlags();

    if (!shouldShowUnicronHomepage(flags, Boolean(willShowUnicronHomepageFlag))) {
      return <ForceFullReload />;
    }

    return (
      <BrazeWrapper>
        <CookiesProvider>
          <Homepage />
        </CookiesProvider>
      </BrazeWrapper>
    );
  },

  async job => {
    const { services, context } = job;
    const { flagr, flags, logger } = services;

    const deferToMegatron = () =>
      proxyToHost(job, env.MEGATRON_CLUSTER_DOMAIN, env.MEGATRON_PROTOCOL);

    const ip = context.req ? clientIP(context.req) ?? 'unknown_ip' : 'unknown_ip';

    const willShowUnicronHomepageFlag = await flagr.booleanEvaluate(HOMEPAGE_FLAG, {
      id: ip,
    });

    if (!shouldShowUnicronHomepage(flags, willShowUnicronHomepageFlag, logger) || !job.server) {
      return await deferToMegatron();
    }

    const promises = new PromiseBag();

    promises.add(preloadPromotedSpecialties(job));

    if (!flags.disableExperiments) {
      promises.add(
        preloadExperiments(job, [
          AnnouncementBannerExperiment,
          HomepageTilesExperiment,
          DesktopTopNavExperiment,
        ]),
      );
    }

    logger.info('Unicron Homepage Rendered');

    await promises.allDone();
  },
);

const shouldShowUnicronHomepage = (
  flags: Flags,
  willShowFlag: boolean,
  logger?: LoggerService,
): boolean => {
  const { newHomepage: newHomePageFlag } = flags;
  const devEnabled = process.env.NODE_ENV === 'development';

  if (newHomePageFlag !== undefined) {
    return newHomePageFlag;
  }

  if (newHomePageFlag || devEnabled || willShowFlag) return true;

  if (logger) logger?.info('Megatron Homepage Rendered');

  return false;
};
